import { useEffect, useMemo, useState, useRef, useCallback } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, Backdrop, Checkbox, CircularProgress, FormControlLabel, Autocomplete, TextField, IconButton, Tooltip } from "@mui/material";
import { EditNote } from "@mui/icons-material";
import { blue, grey, lightBlue, red} from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { cloneDeep, findIndex, isEmpty, isEqual, isNil, isNull, isUndefined, size } from "lodash";

import mpPsiSimulationService from "@plannel/services/mp/mp-psi-simulation-service"
import planVersionService from "@plannel/services/data/plan-version-service";
import runMpService from "@plannel/services/mp/run-mp-service";
import versionNoteService from "@plannel/services/system/version-note-service";
import NotesModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/NotesModal";

import SplitterContainer from "@plannel/components/SplitterContainer";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import { DeleteButton, SaveButton, CheckButton } from "@plannel/components/ActionIconButton";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ZeroExclusionFilter from "@plannel/components/filter/ZeroExclusionFilter";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import ResourceFilter from "@plannel/components/filter/ResourceFilter";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import MpVersionFilter, { reloadMpVersionFilter } from "@plannel/components/filter/MpVersionFilter";
import MpProductionPlanDetailModal from "@plannel/pages/master-plan/mp-modal/MpProductionPlanDetailModal";
import p13nService from "@plannel/services/system/p13n-service";
import DimensAttrModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DimensAttrModal";
import companyService from "@plannel/services/data/company-service";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";

const { snackBarMessageColor } = ColorUtil;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;

// const FINITE = "finite";
const { itemType, mpMeasure, versionStatusCode } = CodeUtil;
const { BOH, BACKORDER_QTY, DEMAND_QTY, PRODUCTION_PLAN, SHIPMENT_QTY, EOH, SIM_PRODUCTION_PLAN, SIM_BOH, SIM_BACKORDER_QTY, SIM_DEMAND_QTY, SIM_EOH, SIM_SUPPLY_QTY } = mpMeasure;
const { CAPA_TOTAL, CAPA_LOAD, CAPA_SETUP, CAPA_OVER, CAPA_OVER_TOTAL, CAPA_AVAIL, CAPA_UTIL, LIMIT_UTIL_RATE } = mpMeasure;
const { PLANNED } = versionStatusCode;
const MAIN_GRID = "mainGrid";
const SUB_GRID = "subGrid";
const PROD_PLAN_KEY = "productionPlanPivotData";
const PROD_PLAN_ADJ_KEY = "productionPlanAdjPivotData";
/* 동시자원(BOR Set) 역할. 이 화면은 주 자원(MAIN)만 조회/수정한다. */
const RES_ROLE_MAIN = "main";

const measureKeyMap = {
  [SHIPMENT_QTY]: "supplyQty",
  [SIM_SUPPLY_QTY]: "simSupplyQty"
};

/* 노트가 달린 셀 표식 — RP Review 와 같은 규격 */
const NOTE_MARKER = "3px solid #4caf50";

// 동결 셀에 마우스 올리면 "동결 구간" 툴팁 표시
const FrozenCellRenderer = ({ value, valueFormatted, label }) => (
  <Tooltip title={label} placement="bottom-start">
    <Box component="span" sx={{ display: "block", width: "100%" }}>{valueFormatted ?? value}</Box>
  </Tooltip>
);

const isFrozenProdCell = (params, frozenMeasures) => {
  const { data, colDef } = params;
  return frozenMeasures.includes(data?.measure) && !isNil(data?.frozenEndDate) && colDef?.field <= data.frozenEndDate;
}

const MAIN_FROZEN_MEASURES = [PRODUCTION_PLAN, SIM_PRODUCTION_PLAN];
const SUB_FROZEN_MEASURES = [SIM_PRODUCTION_PLAN];

const MpPsiSimulation = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [10, 30, 50, 100];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);
  const [sortParams, setSortParams] = useState({});
  const [mpVersionFilter, setMpVersionFilter] = useState(reduxViewState?.mpVersion ? reduxViewState.mpVersion : null);
  const [subGridResourceListFilter, setSubGridResourceListFilter] = useState(reduxViewState?.subGridResourceListFilter ? reduxViewState.subGridResourceListFilter : []);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [appliedExceptions, setAppliedExceptions] = useState(reduxViewState?.appliedExceptions ?? []);
  const itemTypes = [itemType.KIT.cd, itemType.FG.cd, itemType.SFG.cd];
  // itemTypeSearch: 명시적 null(타입 필터 없음 — 예: Resource Gantt 계층 모드 이동)을 FG 기본값과 구분해 유지
  const [itemTypeSearch, setItemTypeSearch] = useState(
    reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "itemTypeSearch") ? reduxViewState.itemTypeSearch : itemType.FG.cd
  );

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [prodPlanDetailOpen, setProdPlanDetailOpen] = useState(false);
  const [prodPlanNodeData, setProdPlanNodeData] = useState({});
  const selectedCellId = useRef([]);

  // 노트 — noteOpen 이 true 면 패널이 닫힌 상태다(RP Review 와 같은 규약)
  const [noteOpen, setNoteOpen] = useState(true);
  const [noteCellData, setNoteCellData] = useState({});
  const [updateNoteData, setUpdateNoteData] = useState({});
  const noteKeySet = useRef(new Set());

  const [backDropOpen, setBackDropOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [simulationData, setSimulationData] = useState([]);
  const [simulationHeaders, setSimulationHeaders] = useState([]);
  const gridHeaderMap = useRef({});
  const simulationUpdateDatasRef = useRef([]);
  const [onlyExceptionApplied, setOnlyExceptionApplied] = useState(reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "onlyExceptionApplied") ? reduxViewState.onlyExceptionApplied : false);
  const [frozenEditApplied, setFrozenEditApplied] = useState(reduxViewState?.frozenEditApplied ?? false);
  const frozenEditAppliedRef = useRef(frozenEditApplied);

  const [selectedMainNode, setSelectedMainNode] = useState([]);
  const mainGridRef = useRef();
  const subGridRef = useRef();
  const mainBucketColumnDefs = useRef([]);
  const subBucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);
  const itemColsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "itemColsHide") ? reduxViewState.itemColsHide : true;

  // item attribute
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  const [dimensionAttrData, setDimensionAttrData] = useState(null);
  const [attrConfig, setAttrConfig] = useState(null);

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [subMeasureModalOpen, setSubMeasureModalOpen] = useState(false);
  const [subMeasures, setSubMeasures] = useState([SIM_PRODUCTION_PLAN, CAPA_UTIL]);
  const allMeasure = useMemo(
    () => [BOH, BACKORDER_QTY, DEMAND_QTY, PRODUCTION_PLAN, SHIPMENT_QTY, EOH, SIM_BOH, SIM_BACKORDER_QTY, SIM_DEMAND_QTY, SIM_PRODUCTION_PLAN, SIM_SUPPLY_QTY, SIM_EOH],
    []
  );
  const subAllMeasure = useMemo(
    () => [SIM_PRODUCTION_PLAN, CAPA_TOTAL, CAPA_OVER_TOTAL, CAPA_LOAD, CAPA_SETUP, CAPA_OVER, CAPA_AVAIL, CAPA_UTIL, LIMIT_UTIL_RATE],
    []
  );
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);

  const [subGridData, setSubGridData] = useState([]);
  const [filterSubGridData, setFilterSubGridData] = useState([]);
  const [customResourceFilterList, setCustomResourceFilterList] = useState([]);
  const [resourceData, setResourceData] = useState([]);

  const ZERO_DEMAND = "zeroDemand";
  const ZERO_PROD = "zeroProd";
  const ZERO_EOH = "zeroEoh";

  const EXCEPTION_LIST = [
    ZERO_DEMAND,
    ZERO_PROD,
    ZERO_EOH
  ];

  const cellStyleByMeasure = (params) => {
    if (params?.data?.measure && params.data?.itemType === itemType.FG.cd) {
      const { measure } = params.data;
      if (isEqual(measure, SIM_PRODUCTION_PLAN)) {
        return { background: lightBlue[50] };
      }
    }
    return {};
  }

  const findMeasureData = (nodes, measure) => {
    for (const node of nodes) {
      if (node && isEqual(node.data.measure, measure)) {
        return node.data;
      }
    }
    return {};
  }

  /* 메인 그리드 버킷 칸의 노트 표식. 동결 구간에도 붙는다(노트는 편집 여부와 무관) */
  const withNoteMarker = (params, cellStyle) => {
    const { data, colDef } = params;
    if (!data || !noteKeySet.current.size) {
      return cellStyle;
    }
    const noteKey = CodeUtil.getNoteKey(viewName, {
      locCd: data.locCd, itemCd: data.itemCd, baseDate: colDef?.field, measure: data.measure
    });
    return noteKeySet.current.has(noteKey) ? { ...(cellStyle ?? {}), borderLeft: NOTE_MARKER } : cellStyle;
  }

  const dataCellStyle = (params, headers) => {
    const cellStyle = cellStyleByMeasure(params);
    const { column, data, node, value } = params;
    if (data && column) {
      const { measure } = data;
      if (measure && column?.colId) {
        const replacePostFix = mpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + mpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
        const replaceColId = column?.colId.replace(replacePostFix, "");
        const headerIndex = headers.indexOf(replaceColId);
        if (headerIndex !== -1 && value !== null) {
          const prodQtyValues = Object.values(data?.[PROD_PLAN_KEY]);
          const prodQty = prodQtyValues.reduce((acc, val) => acc + (val[headerIndex] || 0), 0);
          const bohQty = data.bohQtyPivotData[headerIndex];
          const eohQty = data.eohQtyPivotData[headerIndex];
          const shipmentQty = data.shipmentQtyPivotData[headerIndex];
          const backorderQty = data.backorderQtyPivotData[headerIndex];

          const isSimModified = isEqual(measure, SIM_PRODUCTION_PLAN) && !isEqual(value, prodQty);
          const isSimBohModified = isEqual(measure, SIM_BOH) && !isEqual(value, bohQty);
          const isSimEohModified = isEqual(measure, SIM_EOH) && !isEqual(value, eohQty);
          const isSimShipmentModified = isEqual(measure, SIM_SUPPLY_QTY) && !isEqual(value, shipmentQty);
          const isSimBackorderModified = isEqual(measure, SIM_BACKORDER_QTY) && !isEqual(value, backorderQty);

          if (isSimModified || isSimBohModified || isSimEohModified || isSimShipmentModified || isSimBackorderModified) {
            cellStyle.color = blue.A700;
            cellStyle.fontWeight = 'bold';
          }

          if (isEqual(measure, SIM_SUPPLY_QTY)) {
            const prevHeaderText = headerIndex > 0 ? headers[headerIndex - 1] : null;
            const simBackorderQtyData = findMeasureData(node.parent.allLeafChildren, SIM_BACKORDER_QTY);
            const simBackorderQty = prevHeaderText ? simBackorderQtyData[prevHeaderText] ?? 0 : 0;
            const simDemandQty = node.data.demandQtyAdjPivotData[headerIndex] ?? 0;

            if (simDemandQty + simBackorderQty > value) {
              cellStyle.background = red[100];
              cellStyle.fontStyle = 'italic';
            }
          }
        }
      }
    }

    if (isFrozenProdCell(params, MAIN_FROZEN_MEASURES)) {
      cellStyle.background = grey[300];
    }
    return cellStyle;
  }

  const subGridCellStyle = (params, headers) => {
    const cellStyle = cellStyleByMeasure(params);

    const { column, data, value } = params;
    if (data && column) {
      const colFieldName = column.getColDef().field;
      const headerIndex = headers.indexOf(colFieldName);
      if (headerIndex > -1 && value !== null) {
        const isSimProdModified = isEqual(data.measure, SIM_PRODUCTION_PLAN) && !isEqual(value, data?.productionPlanPivotData[headerIndex]);
        const isCapaUtilMeasure = isEqual(data.measure, CAPA_UTIL);
        const isLimitUtilRateMeasure = isEqual(data.measure, LIMIT_UTIL_RATE);

        if (isSimProdModified) {
          cellStyle.color = blue.A700;
          cellStyle.fontWeight = 'bold';
        }

        if (isCapaUtilMeasure || isLimitUtilRateMeasure) {
          // const verisonRc = mpVersionFilter?.planConfigObj?.RC; // FINITE 체크!~

          if (value > 100) {
            cellStyle.background = red[100];
          }
        }
      }
    }

    if (isFrozenProdCell(params, SUB_FROZEN_MEASURES)) {
      cellStyle.background = grey[300];
    }

    return cellStyle;
  }

  const dynamicCellEditable = (params) => {
    if (!frozenEditAppliedRef.current && isFrozenProdCell(params, SUB_FROZEN_MEASURES)) {
      return false;
    }

    const { measure } = params.data;

    if (measure && params.data?.itemType === itemType.FG.cd) {
      if (isEqual(measure, SIM_PRODUCTION_PLAN)) {
        return true;
      }
    }

    return false;
  }

  const emptyValueFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value)) {
      return "";
    }

    if ([CAPA_UTIL, LIMIT_UTIL_RATE].includes(params.data?.measure)) {
      return NumberUtil.formatNumber(params.value, { maximumFractionDigits: 3, minimumFractionDigits: 0 }) + "%";
    }

    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const getAttributeColDefs = (isExcel = false) => {
    const itemColDefs = [];
    const attrDatas = dimensionAttrData?.attr;

    if (!attrDatas) {
      return itemColDefs;
    }

    for (let i = 0; i < attrDatas?.length; i ++) {
      const attrData = attrDatas[i];
      const attrId = `prod_${attrData}`;
      const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
      const attrNm = attrConfig?.item ? attrConfig.item[attrData] ?? attrData : attrData;
      const fieldNm = attrData;

      const itemAttrArry = isExcel ? [
        {
          headerName: attrNm,
          colId: attrId,
          field: fieldNm,
          cellClass: "stringType",
          width: 120,
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
        }] : [{
        headerName: attrNm,
        colId: attrId,
        showRowGroup: fieldNm,
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true,
        },
        width: 120,
        sortable: true,
        sort
      },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      itemColDefs.push(...itemAttrArry);
    }

    return itemColDefs;
  }

  const defaultColumnDefs = useMemo(() => {

    const attributeColDefs = {
      // column header dimension modal
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          setDimenModalOpen(true);
        }
      },
    }

    const baseColumns = [
      {
        headerName: 'location', colId: "locNmGrp", width: 160, showRowGroup: 'locNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
      },
      {
        headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true },
        headerComponent: CustomDetailHeader,
        headerComponentParams: {
          viewName, reduxKey: 'itemColsHide', colsHide: itemColsHide,
          detailColumns: ["itemNmGrp"]
        },...attributeColDefs
      },
      {
        headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true, hide: itemColsHide,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
      },
      { headerName: "location", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "locCd", field: "locCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "locNm", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "lotSize", field: "lotSize", rowGroup: true, type: ["numberColumn", "rightAligned"], hide: true, suppressColumnsToolPanel: true },
      { headerName: "minLotSize", field: "minLotSize", rowGroup: true, type: ["numberColumn", "rightAligned"], hide: true, suppressColumnsToolPanel: true },
      { headerName: "maxLotSize", field: "maxLotSize", rowGroup: true, type: ["numberColumn", "rightAligned"], hide: true, suppressColumnsToolPanel: true },
      // { headerName: "item", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "itemType", field: "itemType", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      {
        headerName: "measure", field: "measure", width: 170, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
        valueFormatter: params => t(params.value), cellStyle: cellStyleByMeasure, headerComponent: CustomHeaderMenuIcon,
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      }
    ];

    const itemColDefs = getAttributeColDefs();
    if (!isEmpty(itemColDefs)) {
      const itemIdx = baseColumns.findIndex(col => isEqual(col.colId, "itemCdGrp"));
      baseColumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }

    return GridUtils.getColumnDefs(baseColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData, attrConfig, sortParams]);

  /* 하단 Sub Grid */
  const defaultSubGridColumnDefs = [
    {
      headerName: 'location', colId: "locNmGrp", width: 160, showRowGroup: 'locNm', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, reduxKey: 'itemColsHide', colsHide: itemColsHide,
        detailColumns: ["itemNmGrp"]
      }
    },
    {
      headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true, hide: itemColsHide,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
    },
    {
      headerName: "workcenter", colId: "workCenterGrp", width: 160, showRowGroup: "workCenterCd", pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
    },
    {
      headerName: "resName", colId: "resNmGrp", width: 160, showRowGroup: "resNm", pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
    },
    { headerName: "locCd", field: "locCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locNm", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "item", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "workcenter", field: "workCenterCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    { headerName: "resName", field: "resNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "measure", field: "measure", width: 180, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
      valueFormatter: params => t(params.value), cellStyle: cellStyleByMeasure, headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setSubMeasureModalOpen
      }
    }
  ];

  const defaultExportColumnDefs = useMemo(() => {

    const baseColumns = [
      { field: "locName", colId: "locNmGrp", rowGroup: true, hide: true },
      { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true },
      { field: "itemName", colId: "itemNmGrp", rowGroup: true, hide: true },
      { headerName: "location", field: "locNm", width: 140 },
      { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
      { headerName: "itemName", field: "itemNm", width: 140 },
      { headerName: "measure", field: "measure", width: 160, valueFormatter: (params) => t(params.value) }
    ];

    const itemColDefs = getAttributeColDefs(true);
    if (!isEmpty(itemColDefs)) {
      const itemIdx = baseColumns.findIndex(col => isEqual(col.headerName, "itemName"));
      baseColumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }

    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData, attrConfig]);

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (!isEmpty(mpVersionFilter)) {
      params.versionId = mpVersionFilter.id;
      const timeBuckets = mpVersionFilter?.planConfigObj?.TIME_BUCKETS;
      params.timeBuckets = !isEmpty(timeBuckets) ? JSON.stringify(timeBuckets) : null;
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (filterValue?.category) {
      // params.hrchyCd = filterValue.category.hrchyCd;
      params.categoryCds = filterValue?.category?.filter((category) => category?.leafFlg).map((category) => category?.hrchyCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (!isEmpty(itemTypeSearch)) {
      params.itemType = itemTypeSearch;
    }

    if (!isEmpty(appliedExceptions)) {
      params.appliedExceptions = appliedExceptions;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }

    return { ...params, ...sortParams, onlyExceptionApplied };
  }

  const handleDimenClose = () => {
    setDimenModalOpen(false);
  };

  const setGridHeaders = (headers, headerMap) => {
    const mainColDefs = cloneDeep(defaultColumnDefs);
    const subColDefs = cloneDeep(defaultSubGridColumnDefs);
    mainBucketColumnDefs.current = [];
    subBucketColumnDefs.current = [];

    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = mpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + mpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;

    headers.forEach((header) => {
      mainBucketColumnDefs.current.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        suppressMovable: false,
        type: ["nonEditableColumn", "rightAligned"],
        cellStyle: (params) => withNoteMarker(params, dataCellStyle(params, headers)),
        cellClassRules: { greyBackground: params => isFrozenProdCell(params, MAIN_FROZEN_MEASURES) },
        cellRendererSelector: params => isFrozenProdCell(params, MAIN_FROZEN_MEASURES)
          ? { component: FrozenCellRenderer, params: { label: GridUtils.gridValueL10N("frozenRangeSim") } }
          : undefined,
        cellEditor: NumericEditor,
        valueFormatter: emptyValueFormatter
      });

      subBucketColumnDefs.current.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        suppressMovable: false,
        type: ["nonEditableColumn", "rightAligned"],
        cellStyle: (params) => subGridCellStyle(params, headers),
        cellRendererSelector: params => isFrozenProdCell(params, SUB_FROZEN_MEASURES)
          ? { component: FrozenCellRenderer, params: { label: GridUtils.gridValueL10N("frozenRangeSim") } }
          : undefined,
        editable: (params) => dynamicCellEditable(params),
        cellEditor: NumericEditor,
        valueFormatter: emptyValueFormatter
      });
    });

    GridUtils.setColumnDefs({ ...mainGridRef.current, columnDefs: [...mainColDefs, ...mainBucketColumnDefs.current] });
    GridUtils.setColumnDefs({ ...subGridRef.current, columnDefs: [...subColDefs, ...subBucketColumnDefs.current] });
  }

  const setGridDatas = (headers, results, isResource = false) => {
    const datas = [];
    const productionPlanPivotData = "productionPlanPivotData";
    const productionPlanAdjPivotData = "productionPlanAdjPivotData";
    const backorderQtyPivotData = "backorderQtyPivotData";
    const backorderQtyAdjPivotData = "backorderQtyAdjPivotData";
    const demandQtyPivotData = "demandQtyPivotData";
    const demandQtyAdjPivotData = "demandQtyAdjPivotData";
    const shipmentQtyPivotData = "shipmentQtyPivotData";
    const shipmentQtyAdjPivotData = "shipmentQtyAdjPivotData"; // shipmentQtyAdj === supplyQtyAdj
    const bohQtyPivotData = "bohQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";
    const bohQtyAdjPivotData = "bohQtyAdjPivotData";
    const eohQtyAdjPivotData = "eohQtyAdjPivotData";

    //resource
    const capaTotalPivotData = "capaTotalPivotData";
    const capaOverTotalPivotData = "capaOverTotalPivotData";
    const capaLoadPivotData = "capaLoadPivotData";
    const capaSetupPivotData = "capaSetupPivotData";
    const capaOverPivotData = "capaOverPivotData";
    const capaAvailPivotData = "capaAvailPivotData";
    const capaUtilPivotData = "capaUtilPivotData";
    const limitUtilRatePivotData = "limitUtilRatePivotData";

    for (const resultsData of results) {
      let measure = isEmpty(measures) ? allMeasure : measures;
      if (isResource) {
        measure = [capaTotalPivotData, capaOverTotalPivotData, capaLoadPivotData, capaSetupPivotData, capaOverPivotData, capaAvailPivotData, capaUtilPivotData, limitUtilRatePivotData];
      }

      for (const measureName of measure) {
        const translationKey = measureKeyMap[measureName] || measureName;
        const data = { ...resultsData, measure: translationKey };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {

            case BOH:
              data[header] = resultsData[bohQtyPivotData][idx];
              break;

            case BACKORDER_QTY:
              data[header] = resultsData[backorderQtyPivotData][idx];
              break;

            case DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;

            case PRODUCTION_PLAN: {
              const prodPlanValues = Object.values(resultsData[productionPlanPivotData]);
              data[header] = prodPlanValues.reduce((acc, val) => acc + (val[idx] || 0), 0);
              break;
            }
            case SHIPMENT_QTY:
              data[header] = resultsData[shipmentQtyPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            case SIM_BOH:
              data[header] = resultsData[bohQtyAdjPivotData][idx];
              break;

            case SIM_BACKORDER_QTY:
              data[header] = resultsData[backorderQtyAdjPivotData][idx];
              break;

            case SIM_DEMAND_QTY:
              data[header] = resultsData[demandQtyAdjPivotData][idx];
              break;

            case SIM_PRODUCTION_PLAN: {
              const values = Object.values(resultsData[productionPlanAdjPivotData]);
              data[header] = values.reduce((acc, val) => acc + (val[idx] || 0), 0);
              break;
            }
            case SIM_SUPPLY_QTY:
              data[header] = resultsData[shipmentQtyAdjPivotData][idx]; // shipmentQtyAdj === supplyQtyAdj
              break;

            case SIM_EOH:
              data[header] = resultsData[eohQtyAdjPivotData][idx];
              break;

            case capaTotalPivotData:
              data[header] = resultsData[capaTotalPivotData][idx];
              data.measure = CAPA_TOTAL;
              break;

            case capaOverTotalPivotData:
              data[header] = resultsData[capaOverTotalPivotData][idx];
              data.measure = CAPA_OVER_TOTAL;
              break;

            case capaLoadPivotData:
              data[header] = resultsData[capaLoadPivotData][idx];
              data.measure = CAPA_LOAD;
              break;

            case capaSetupPivotData:
              data[header] = resultsData[capaSetupPivotData][idx];
              data.measure = CAPA_SETUP;
              break;

            case capaOverPivotData:
              data[header] = resultsData[capaOverPivotData][idx];
              data.measure = CAPA_OVER;
              break;

            case capaAvailPivotData:
              data[header] = resultsData[capaAvailPivotData][idx];
              data.measure = CAPA_AVAIL;
              break;

            case capaUtilPivotData:
              data[header] = resultsData[capaUtilPivotData][idx];
              data.measure = CAPA_UTIL;
              break;

            case limitUtilRatePivotData:
              data[header] = resultsData[limitUtilRatePivotData][idx];
              data.measure = LIMIT_UTIL_RATE;
              break;

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retrievePsiSimulation = () => {
    mainGridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    mpPsiSimulationService.getAll(params)
      .then(response => {

        const { pivotResult, resource, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;

        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          setSimulationHeaders(localDateHeader);
          setGridHeaders(localDateHeader, headerMap);

          const datas = setGridDatas(localDateHeader, data);
          setSimulationData(datas);

          const resourceChart = setGridDatas(localDateHeader, resource, true);
          setResourceData(resourceChart);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          setSimulationHeaders([]);
          setSimulationData([]);
          setSubGridData([]);
          setResourceData([]);
          setCustomResourceFilterList([]);
          GridUtils.setColumnDefs({ ...mainGridRef.current, columnDefs: defaultColumnDefs });
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        GridUtils.setColumnDefs({ ...mainGridRef.current, columnDefs: defaultColumnDefs });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { filter: filterValue, subGridResourceListFilter, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, itemTypeSearch, currentPage, pageSize, onlyExceptionApplied, frozenEditApplied, appliedExceptions });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        simulationUpdateDatasRef.current = [];
        mainGridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const getCustomConfig = () => {
    companyService.getCustomConfig()
        .then(response => {
          if (response.data) {
            setAttrConfig(response.data);
          } else {
            setAttrConfig({});
          }
        })
        .catch(e => {
          debug(e);
          setAttrConfig({});
        });
  };

  const retrieveAttributeConfig = () => {
    p13nService.get(`${viewName}_ETC`)
        .then(response => {
          if (response.data) {
            const attrConfig = response?.data?.ATTRIBUTE;
            setDimensionAttrData(attrConfig ? response.data.ATTRIBUTE : {});
          }
        })
        .catch(e => {
          debug(e);
          setDimensionAttrData({});
        });
  };

  const deleteVersion = () => {
    setBackDropOpen(true);

    planVersionService.deleteVersionWithData([mpVersionFilter?.id])
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });

        reloadMpVersionFilter(reduxDispatch);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        debug(e);
      })
      .then(() => {
        setBackDropOpen(false);
      });
  }

  const confirmVersion = (isSaved = false) => {
    if (!isSaved) {
      setBackDropOpen(true);
    }

    runMpService.simulation(mpVersionFilter)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_ConfirmSuccess") });
        setBackDropOpen(false);
        reloadMpVersionFilter(reduxDispatch);
        retrievePsiSimulation();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
        setBackDropOpen(false);
      });
  }

  const updateSimulationData = (confirmationPending = false) => {
    setBackDropOpen(true);
    // id 값이 Long 타입이 아닌경우 NULL 처리
    const updateDataParam = (simulationUpdateDatasRef.current || []).map(data => {
      if (data.id && isNaN(Number(data.id))) {
        return { ...data, id: null };
      }
      return data;
    });

    mpPsiSimulationService.updateSimulationData(updateDataParam)
      .then(() => {
        if (confirmationPending) {
          confirmVersion(true);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          retrievePsiSimulation();
        }

        reloadMpVersionFilter(reduxDispatch);
        simulationUpdateDatasRef.current = [];
      })
      .catch(e => {
        debug(e);
        const detailErrorMsgInfo = e.response?.data?.message?.message ? { content: t(e.response?.data?.message?.message) } : {};
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed"), ...detailErrorMsgInfo });
      }).then(() => {
        if (!confirmationPending) {
          setBackDropOpen(false);
        }
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      if (isEmpty(simulationUpdateDatasRef.current)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      } else {
        updateSimulationData();
      }
    } else if (action === "DELETE") {
      deleteVersion();
    } else if (action === "CONFIRM") {
      confirmVersion();
    } else if (action === "SAVE_AND_CONFIRM") {
      updateSimulationData(true);
    }
  }

  const onClickConfirmButton = () => {
    if (isEmpty(simulationUpdateDatasRef.current)) {
      setDialogInfo({ open: true, actionName: "CONFIRM", title: "confirm", content: t("MSG_RunSimulConfirm") });
    } else {
      setDialogInfo({ open: true, actionName: "SAVE_AND_CONFIRM", title: "confirm", content: t("MSG_RunSimulConfirmAfterSaving") });
    }
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    setOnlyExceptionApplied(value);
  }

  const handleFrozenEditCheckbox = e => {
    const value = e.target.checked;
    frozenEditAppliedRef.current = value;
    setFrozenEditApplied(value);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, { frozenEditApplied: value })));
    subGridRef.current?.api?.stopEditing(true);
  }

  const onGridReady = (params, target) => {
    setGridLoading(true);

    if (isEqual(target, MAIN_GRID)) {
      GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
    } else {
      GridUtils.setColumnDefs({ ...params, columnDefs: defaultSubGridColumnDefs, viewName, initState: true });
    }

  }

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({nodes: selected, newValue: true, source: CHECKBOX_SELECTED});

    // 버킷 칸을 누르면 노트 패널의 대상 셀이 된다
    const cellDate = e.colDef?.field;
    const target = e.data;
    if (target && !isEmpty(gridHeaderMap.current[cellDate])) {
      setNoteCellData({
        cellDate,
        // MP 버킷은 일 · 주가 섞인다. 노트 패널의 날짜 표시는 주 형식(yyyy-MM-dd)이 둘 다 담는다
        timeDimension: CodeUtil.buckets.WEEK,
        versionDto: { ...mpVersionFilter },
        measure: target.measure,
        itemCd: target.itemCd,
        itemNm: target.itemNm,
        locCd: target.locCd,
        locNm: target.locNm,
        baseDate: cellDate,
      });
    }
  };

  /* 이 버전에 노트가 달린 셀 키. 셀 표식(withNoteMarker)이 쓴다 */
  const retrieveNoteKeys = () => {
    if (isEmpty(mpVersionFilter)) {
      return;
    }
    versionNoteService.list({ planVersionId: mpVersionFilter.id, moduleCds: ["MP"] })
      .then(response => {
        noteKeySet.current = new Set((response.data?.result ?? []).map(note => note.noteKey));
        mainGridRef.current?.api?.refreshCells({ force: true });
      })
      .catch(e => debug(e));
  };

  const handleNoteModal = () => {
    setNoteOpen(!noteOpen);
  };

  useEffect(() => {
    noteKeySet.current = new Set();
    retrieveNoteKeys();
    setNoteCellData({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter?.id]);

  // 노트를 저장 · 삭제하면 그 셀의 표식만 고친다(재조회 없음)
  useEffect(() => {
    if (size(updateNoteData) > 0) {
      const noteKey = CodeUtil.getNoteKey(viewName, updateNoteData);
      if (updateNoteData.deleteFlg) {
        noteKeySet.current.delete(noteKey);
      } else {
        noteKeySet.current.add(noteKey);
      }
      mainGridRef.current?.api?.refreshCells({ force: true });
      setUpdateNoteData({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoteData]);

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      /* 클릭한 Row에서 이용가능한 Resource를 FIlter값에 넣어줌. */
      const availableResourceList = params?.data?.availableResourceList ? params.data.availableResourceList : [];
      setCustomResourceFilterList(availableResourceList);

      const filterInfo = extractFilterInfo(params, t, mainGridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      setSelectedMainNode([]);

      const resourceMeasureCheck = (measure) => {
        const subMeasureFilter = isEmpty(subMeasures) ? subAllMeasure : subMeasures;
        if (subMeasureFilter.includes(measure)) {
          return isEqual(measure, CAPA_TOTAL) || isEqual(measure, CAPA_OVER_TOTAL) || isEqual(measure, CAPA_LOAD) || isEqual(measure, CAPA_SETUP) || isEqual(measure, CAPA_OVER) || isEqual(measure, CAPA_AVAIL) || isEqual(measure, CAPA_UTIL) || isEqual(measure, LIMIT_UTIL_RATE);
        }

        return false;
      }

      const resourceKeyCheck = (resParam, leafData, leafResCd) => {
        return isEqual(resParam.locCd, leafData.locCd) && isEqual(resParam.resCd, leafResCd);
      }

      const subGridRows = [];
      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        const leafData = leaf.data;
        selectedRows.push(leafData);

        if (leafData?.measure === SIM_PRODUCTION_PLAN) {
          setSelectedMainNode(leaf); // leaf is Node

          const resMap = leafData.resMap;
          const workCenterMap = leafData.workCenterMap;
          const lotSizeMap = leafData.lotSizeMap;
          for (const resCd of Object.keys(resMap)) {
            const lotObj = lotSizeMap[resCd] || {};
            const lotSize = lotObj?.lotSize;
            const minLotSize = lotObj?.minLotSize;
            const maxLotSize = lotObj?.maxLotSize;

            const timeBucketData = {};
            const workCenterCd = workCenterMap[resCd];
            const resName = resMap[resCd];
            const prodArray = leafData?.[PROD_PLAN_KEY]?.[resCd] || [];
            const simProdArray = leafData?.[PROD_PLAN_ADJ_KEY]?.[resCd] || [];

            const headerKeyArray = Object.keys(gridHeaderMap.current);
            headerKeyArray.sort();
            /* Resource 별 Grid 데이터 생성 */
            for (let idx = 0; idx < headerKeyArray.length; idx++) {
              timeBucketData[headerKeyArray[idx]] = simProdArray[idx] || 0;
            }

            const subGridData = {
              locCd: leafData.locCd,
              locNm: leafData.locNm,
              workCenterCd,
              itemCd: leafData.itemCd,
              itemNm: leafData.itemNm,
              itemType: leafData.itemType,
              lotSize,
              minLotSize,
              maxLotSize,
              resCd,
              resNm: resName,
              measure: SIM_PRODUCTION_PLAN,
              frozenEndDate: leafData.frozenEndDateMap?.[resCd],
              productionPlanPivotData: prodArray,
              ...timeBucketData
            }

            subGridRows.push(subGridData);

            /* Resource 상태 값 데이터도 추가. */
            for (const resource of resourceData) {
              if (resourceMeasureCheck(resource.measure) && resourceKeyCheck(resource, leafData, resCd)) {
                const resourceCopy = cloneDeep(resource);
                resourceCopy.itemCd = leafData.itemCd;
                resourceCopy.itemNm = leafData.itemNm;
                resourceCopy.workCenterCd = workCenterCd;
                resourceCopy.locCd = leafData.locCd;
                resourceCopy.locNm = leafData.locNm;
                resourceCopy.resNm = resName;

                subGridRows.push(resourceCopy);
              }
            }
          }
        }
      }

      // psi
      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });

      setSubGridData(subGridRows);
    }
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0)
      return;

    const firstNode = params.api.getDisplayedRowAtIndex(0);
    if (firstNode) {
      const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
      params.api.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
    }
  }

  const applyMeasures = (selectedMeasures) => {
    setMeasures(selectedMeasures);
  }

  const applySubMeasures = (selectedSubMeasures) => {
    setSubMeasures(selectedSubMeasures);
  }

  const addUpdateDatas = datas => {
    const updateDatas = cloneDeep(simulationUpdateDatasRef.current);

    for (const data of datas) {
      const idx = findIndex(updateDatas, updateData => isEqual(updateData.id, data.id));

      if (idx > -1) {
        updateDatas[idx] = { ...updateDatas[idx], ...data };
      } else {
        updateDatas.push(data);
      }
    }

    simulationUpdateDatasRef.current = updateDatas;
  }

  /* return value is Array[] */
  const makeUpdateData = (headerIndex, resultIdData, simBackorderQtyData, simProdQtyData, simShipmentData, simEohData, simBohData, verNumData, invResultIdData, invVerNumData, dataObj, baseDate) => {
    const simulationHeader = simulationHeaders[headerIndex];
    const updateDataArray = [];

    /* Inventory 용은 Resource가 존재하지 않고 관리되기때문에 생산 수량은 생산 Row에, 재고관련은 재고 Row에 저장하기 위함. */
    const invUpdateData = {};
    if (invResultIdData && invVerNumData) {
      invUpdateData.id = invResultIdData[headerIndex];
      invUpdateData.backorderQtyAdj = simBackorderQtyData[simulationHeader];
      invUpdateData.shipmentQtyAdj = simShipmentData[simulationHeader]; // shipmentQtyAdj === supplyQtyAdj
      invUpdateData.bohQtyAdj = simBohData[simulationHeader];
      invUpdateData.eohQtyAdj = simEohData[simulationHeader];
      invUpdateData.verNum = invVerNumData[headerIndex];

      updateDataArray.push(invUpdateData);
    }

    const prodUpdateData = {};
    prodUpdateData.id = resultIdData[headerIndex];

    /* (Insert) New Result Save시 필요한 데이터 */
    prodUpdateData.planVerId = mpVersionFilter.id;
    prodUpdateData.locCd = dataObj.locCd;
    prodUpdateData.locName = dataObj.locNm;
    prodUpdateData.itemCd = dataObj.itemCd;
    prodUpdateData.itemName = dataObj.itemNm;
    prodUpdateData.resCd = dataObj.resCd;
    prodUpdateData.resName = dataObj.resNm;
    /*
      동시자원(BOR Set) 역할. 화면은 주 자원(MAIN)만 다루므로 항상 MAIN 으로 저장한다.
      res_role 이 z_mp_result UK 에 포함되므로 값을 비우면 엔진이 만든 MAIN 행과 별도 행으로 Insert 되어 중복된다.
    */
    prodUpdateData.resRole = RES_ROLE_MAIN;
    prodUpdateData.baseDate = simulationHeader;
    /* (Insert) New Result Save시 필요한 데이터 */

    prodUpdateData.prodQtyAdj = simProdQtyData[simulationHeader];
    prodUpdateData.verNum = verNumData[headerIndex];
    updateDataArray.push(prodUpdateData);

    return updateDataArray;
  }

  /* params: 수정한곳의 params, subGridParentParams: 서브 그리드에서 수정시 상위 컴포넌트 Params */
  const onCellValueChanged = (params) => {
    let locationItemNodes = params.node.parent.allLeafChildren;
    const { column, value } = params;
    let { data, node } = params;

    const { lotSize, minLotSize, maxLotSize } = data;
    const replacePostFix = mpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + mpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    const replaceColId = column?.colId.replace(replacePostFix, "");

    const headerIndex = findIndex(simulationHeaders, header => isEqual(header, replaceColId));
    let prodQtyConsiderLotSize = CodeUtil.calculateLotSize(value, minLotSize, maxLotSize, lotSize); // 사용자가 입력한 값에 Lot Size를 고려한 생산 수량
    const confirmedValue = data?.[PROD_PLAN_KEY]?.[headerIndex] || 0; // 이전에 확정된 값 이하로 수정이 불가능하기 때문에 변수 체크 값

    /* 확정된 생산 수량 값보다 입력한 값이 작은경우 기존값으로 roll-back */
    if (confirmedValue > prodQtyConsiderLotSize) {
      prodQtyConsiderLotSize = confirmedValue;

      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ProductionPlanAdj", {N: confirmedValue}) });
    }

    data[replaceColId] = prodQtyConsiderLotSize; // 현재 수정중인 cell 기준의 데이터
    subGridRef.current.api.redrawRows({ rowNodes: locationItemNodes }); // lot size 고려하여 수정된 데이터로 redraw

    /* Sub Grid 에서 접근하는경우 부모의 데이터를 변경해야하기때문에 Parent Params 을 추가하여 설정. */
    if (!isNil(selectedMainNode)) {
      locationItemNodes = selectedMainNode.parent.allLeafChildren;
      data = selectedMainNode.data;
      data.resCd = params.data.resCd;
      data.resNm = params.data.resNm;
      node = selectedMainNode;
    }

    const { measure, resMap } = data;

    if (measure) {
      if (headerIndex > -1) {
        const isAllowedBackorder = mpVersionFilter?.planConfigObj?.BACKORDER === "allow";

        const simBohData = findMeasureData(locationItemNodes, SIM_BOH);
        const simBackorderQtyData = findMeasureData(locationItemNodes, SIM_BACKORDER_QTY);
        const simDemandQtyData = findMeasureData(locationItemNodes, SIM_DEMAND_QTY);
        const simProdAggregateQtyData = findMeasureData(locationItemNodes, SIM_PRODUCTION_PLAN);
        const simProdModifiedData = params.data;

        const simShipmentQtyData = findMeasureData(locationItemNodes, SIM_SUPPLY_QTY);
        const simEohData = findMeasureData(locationItemNodes, SIM_EOH);

        const resultIdData = data?.idPivotData?.[data.resCd] || [];
        const verNumData = data?.verNumPivotData?.[data.resCd] || [];
        const invResultIdData = data?.invIdPivotData || []; // Resource 코드가 존재하지 않는 결과값은 Inventory 데이터.
        const invVerNumData = data?.invVerNumPivotData || []; // Resource 코드가 존재하지 않는 결과값은 Inventory 데이터.

        const prevHeaderText = headerIndex > 0 ? simulationHeaders[headerIndex - 1] : null;
        const currentHeaderText = simulationHeaders[headerIndex];
        const bucketSize = simulationHeaders.length;
        const datas = [];

        if (isEqual(measure, SIM_PRODUCTION_PLAN)) {
          // 수정한 Bucket 의 Sim. Boh, Sim. Eoh 수정 및 입력 Prod Qty lot size 적용
          let simProdQty = prodQtyConsiderLotSize; // 현재 수정중인 cell 기준의 데이터

          data.productionPlanAdjPivotData[data.resCd][headerIndex] = simProdQty; // 서브 그리드에서 수정시 productionPlanAdjPivotData 에도 반영

          // 자신의 데이터를 제외하고 나머진 rescd의 데이터 값을 가져옴
          let otherProdPlanAdjData = 0;
          for (const otherResCd in resMap) {
            if (otherResCd !== data.resCd) {
              otherProdPlanAdjData += data.productionPlanAdjPivotData[otherResCd][headerIndex];
            }
          }

          // 현재 수정중인 cell 기준의 데이터에 다른 resCd 의 데이터를 더함
          simProdQty += otherProdPlanAdjData;


          simProdAggregateQtyData[currentHeaderText] = simProdQty;

          if (!isEqual(prodQtyConsiderLotSize, simProdQty)) {
            setTimeout(() => { mainGridRef.current.api.flashCells({ rowNodes: [node], columns: [replaceColId], fadeDelay: 1000 }); }, 300);
          }

          const simBohQty = simBohData[currentHeaderText];
          const simPrevBackorderQty = isAllowedBackorder && prevHeaderText ? simBackorderQtyData[prevHeaderText] : 0;
          const simDemandQty = simDemandQtyData[currentHeaderText];

          const simReqQty = simPrevBackorderQty + simDemandQty;
          const simAvailQty = simBohQty + simProdQty;
          const simShipmentQty = Math.min(simReqQty, simAvailQty);
          const simEohQty = simAvailQty - simShipmentQty;

          if (isAllowedBackorder) {
            simBackorderQtyData[currentHeaderText] = simReqQty - simShipmentQty;
          }
          simShipmentQtyData[currentHeaderText] = simShipmentQty;
          simEohData[currentHeaderText] = simEohQty;
        } else if (isEqual(measure, SIM_SUPPLY_QTY)) {
          const simBohQty = simBohData[currentHeaderText];
          const simPrevBackorderQty = isAllowedBackorder && prevHeaderText ? simBackorderQtyData[prevHeaderText] : 0;
          const simDemandQty = simDemandQtyData[currentHeaderText];
          const simProdQty = simProdAggregateQtyData[currentHeaderText];
          const simEohQty = simBohQty + simProdQty - prodQtyConsiderLotSize;

          if (isAllowedBackorder) {
            simBackorderQtyData[currentHeaderText] = simPrevBackorderQty + simDemandQty - prodQtyConsiderLotSize;
          }
          simShipmentQtyData[currentHeaderText] = prodQtyConsiderLotSize;
          simEohData[currentHeaderText] = simEohQty;
        }

        const updateData = makeUpdateData(headerIndex, resultIdData, simBackorderQtyData, simProdModifiedData, simShipmentQtyData, simEohData, simBohData, verNumData, invResultIdData, invVerNumData, data);
        datas.push(...updateData);

        // 이후 bucket sim boh, eoh 수정
        for (let idx = headerIndex + 1; idx < bucketSize; idx++) {
          if (idx > 0) {
            simBohData[simulationHeaders[idx]] = simEohData[simulationHeaders[idx - 1]];
          }

          const prevText = idx > 0 ? simulationHeaders[idx - 1] : null;
          const headerText = simulationHeaders[idx];

          const simBohQty = simBohData[headerText];
          const simPrevBackorderQty = isAllowedBackorder && prevText ? simBackorderQtyData[prevText] : 0;
          const simDemandQty = simDemandQtyData[headerText];
          const simProdQty = simProdAggregateQtyData[headerText];

          const simReqQty = simPrevBackorderQty + simDemandQty;
          const simAvailQty = simBohQty + simProdQty;
          const simShipmentQty = Math.min(simReqQty, simAvailQty);
          const simEohQty = simAvailQty - simShipmentQty;

          if (isAllowedBackorder) {
            simBackorderQtyData[headerText] = simReqQty - simShipmentQty;
          }
          simShipmentQtyData[headerText] = simShipmentQty;
          simEohData[headerText] = simEohQty;

          const recalculatedData = makeUpdateData(idx, resultIdData, simBackorderQtyData, simProdModifiedData, simShipmentQtyData, simEohData, simBohData, verNumData, invResultIdData, invVerNumData, data);

          datas.push(...recalculatedData);
        }

        addUpdateDatas(datas);

        mainGridRef.current.api.redrawRows({ rowNodes: locationItemNodes });
        node.setSelected(false);
        node.setSelected(true);
      }
    }
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrievePsiSimulation();
    }
  }

  const mainStatusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subStatusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationData, viewName]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, columnDefs: defaultColumnDefs });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading) {
      const subGridFilteringData = [];
      if (!isEmpty(subGridResourceListFilter)) {
        const resourceCdList = subGridResourceListFilter.map(resource => resource.resCd);

        for (const subData of subGridData) {
          const resCd = subData.resCd;
          if (isEmpty(resourceCdList) || resourceCdList.includes(resCd)) {
            subGridFilteringData.push(subData);
          }
        }
      } else {
        /* 설정되어있는 Resource Filter가 존재하지 않는다면 Sub Grid Data를 그대로 보여줌 */
        subGridFilteringData.push(...subGridData);
      }

      setFilterSubGridData(subGridFilteringData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subGridResourceListFilter])

  useEffect(() => {
    getCustomConfig();
    retrieveAttributeConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter) && !isEmpty(measures) && dimensionAttrData !== null && attrConfig !== null) {
      retrievePsiSimulation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, measures, sortParams, dimensionAttrData, attrConfig, itemTypeSearch]);

  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter) && !isEmpty(measures)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter?.id, filterValue, onlyExceptionApplied, appliedExceptions]);

  useEffect(() => {
    if (reduxViewState) {
      const currentReduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 다른 화면(Resource Gantt 우클릭 → MP PSI Simulation)에서 조회조건을 넘겨받아 재조회한다.
  // 이미 열린 탭은 재마운트되지 않아 viewState 를 다시 읽지 않으므로, 이벤트로 상태를 갱신해 조회 effect 를 재실행한다.
  useEffect(() => {
    const onPsiSearch = (e) => {
      const d = e?.detail ?? {};
      itemSearch.current = d.itemSearch ?? "";
      extraItemCodesRef.current = d.extraItemCodes ?? [];
      // 명시적 null = 타입 필터 없음(전체) — 키가 없을 때만 FG 기본값
      setItemTypeSearch(Object.prototype.hasOwnProperty.call(d, "itemTypeSearch") ? d.itemTypeSearch : itemType.FG.cd);
      setCurrentPage(1);
      setFilterValue(d.filter ?? { locations: null, category: null });
      if (d.mpVersion) setMpVersionFilter(d.mpVersion);
    };
    window.addEventListener("plannel:mpPsiSearch", onPsiSearch);
    return () => window.removeEventListener("plannel:mpPsiSearch", onPsiSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridLoading && !isEmpty(subMeasures) && !isEmpty(selectedMainNode)) {
      selectedMainNode?.setSelected(true, true, CHECKBOX_SELECTED);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMeasures])

  const onCellSelected = () => {
    const api = subGridRef.current.api;
    const ranges = api.getCellRanges();

    if (ranges && ranges.length > 0) {
      const firstRange = ranges[0];
      const columns = firstRange.columns;
      const colIds = [];
      for (const col of columns) {
        colIds.push(col?.colDef?.field);
      }
      selectedCellId.current = colIds
    }
  }

  const handleProdPlanDetail = () => {
    setProdPlanDetailOpen(true);
  };

  const getContextMenuItems = useCallback((params) => {
    const nodeData = params?.node?.data;
    const targetFields = isEmpty(selectedCellId.current) ? [params?.column?.getColDef()?.field] : selectedCellId.current;
    const frozenLocked = !frozenEditAppliedRef.current
      && targetFields.some(field => isFrozenProdCell({ data: nodeData, colDef: { field } }, SUB_FROZEN_MEASURES));
    const editable = nodeData?.measure === "simProdQty" && !frozenLocked;
    return [
      ...(params.defaultItems || []),
      "separator",
      {
        icon: '<i class="ag-icon ag-icon-plus"></i>',
        disabled: !editable,
        action: () => {
          setProdPlanNodeData({
            ...params?.node?.data,
            selectedCellId: selectedCellId.current
          });
          handleProdPlanDetail();
        },
        name: t("productionPlanDetail"),
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <MpVersionFilter viewName={viewName} mpVersionValue={mpVersionFilter} onChangeFilter={setMpVersionFilter} statusFilters={[PLANNED]} />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs />
        <Autocomplete
          key={"item-type-auto-complete"}
          options={itemTypes}
          getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
          value={itemTypeSearch}
          disableClearable
          onChange={(_, newValue) => setItemTypeSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 240 }}
          renderInput={(params) => (
            <TextField {...params} label={t("itemType")} />
          )}
        />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <ZeroExclusionFilter exceptionList={EXCEPTION_LIST} appliedExceptions={appliedExceptions} setAppliedExceptions={setAppliedExceptions} />
        <FormControlLabel control={<Checkbox color="primary" name={"exceptionsOnly"} checked={onlyExceptionApplied} onChange={handleCheckbox} />} label={t("exceptionsOnly")} key="EXCEPTIONS_ONLY" sx={{ mr: 1, display: "flex" }} />
        <FormControlLabel control={<Checkbox color="primary" name={"editFrozenRange"} checked={frozenEditApplied} onChange={handleFrozenEditCheckbox} />} label={t("editFrozenRange")} key="EDIT_FROZEN_RANGE" sx={{ mr: 1, display: "flex" }} />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          <Tooltip title={t("notes")}>
            <IconButton size="small" color="info" onClick={handleNoteModal}>
              <EditNote />
            </IconButton>
          </Tooltip>
          <ExcelExportButton title={title} service={mpPsiSimulationService} setExcelData={setGridDatas} columnDefs={[...defaultExportColumnDefs, ...mainBucketColumnDefs.current]}
            hasUnsavedChanges={!isEmpty(simulationUpdateDatasRef.current)} totalRows={resultTotalCount.current * rowsPerCombination}
            params={{ ...getRequestParams(false), measureCount: rowsPerCombination }} />
          <DeleteButton disabled={isEmpty(mpVersionFilter)} onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_VersionPermanentlyDelete", { version: mpVersionFilter?.name }) })} />
          <SaveButton disabled={isEmpty(mpVersionFilter)} onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <CheckButton label="confirm" disabled={isEmpty(mpVersionFilter)} onClick={onClickConfirmButton} />
        </Box>
      </FilterContainer>
      <Box className={"grid-area"} sx={{ display: "flex" }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
        <SplitterContainer>
          <Box className="ag-theme-alpine" sx={{ height: "100%" }}>
            <Box className="ag-theme-balham non-background-selected-row" mb={1} sx={{ height: "calc(100% - 50px)" }}>
              <AgGridReact
                ref={mainGridRef}
                rowData={simulationData}
                {...defaultGridMemo}
                columnDefs={defaultColumnDefs}
                groupDisplayType={'custom'}
                groupDefaultExpanded={-1}
                groupHideOpenParents
                rowSelection={{
                  ...defaultGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                statusBar={mainStatusBar}
                onRowSelected={onRowSelected}
                onGridReady={(e) => onGridReady(e, MAIN_GRID)}
                onCellClicked={onCellGridClicked}
                onRowDataUpdated={initCheck}
                onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
                cellSelection={{
                  range: true,
                  suppressMultiRanges: true
                }}
              >
              </AgGridReact>
            </Box>
            <PaginationContainer
              currentPage={currentPage} totalPages={gridPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
          <Box id="psi-simulation-chart" sx={{ height: "100%" }}>
            <Box sx={{mt : 2}}>
              <ResourceFilter viewName={viewName} resourceValue={subGridResourceListFilter} onChangeFilter={setSubGridResourceListFilter} isCustomerResourceFilter isMultiSelect customResourceFilterList={customResourceFilterList} />
            </Box>
            <Box className="ag-theme-balham non-background-selected-row" mt={1} mb={2} sx={{ height: "calc(100% - 68px)" }}>
              <AgGridReact
                ref={subGridRef}
                {...defaultGridMemo}
                groupDisplayType="custom"
                groupDefaultExpanded={-1}
                groupHideOpenParents
                rowSelection={{
                  ...defaultGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                statusBar={subStatusBar}
                onGridReady={(e) => onGridReady(e, SUB_GRID)}
                onCellValueChanged={onCellValueChanged}
                rowData={isEmpty(subGridResourceListFilter) ? subGridData : filterSubGridData}
                getContextMenuItems={getContextMenuItems}
                onCellSelectionChanged={onCellSelected}
              />
            </Box>
          </Box>
        </SplitterContainer>
        </Box>
        {!noteOpen ? (
          <NotesModal
            version={mpVersionFilter}
            timeDimension={CodeUtil.buckets.WEEK}
            cellData={noteCellData}
            pageCd={viewName}
            setUpdateCellData={setUpdateNoteData}
            close={handleNoteModal}
          />
        ) : null}
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      {/* Main Grid Measure Modal */}
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        requiredMeasure={[SIM_PRODUCTION_PLAN, SIM_SUPPLY_QTY, SIM_BOH, SIM_EOH, SIM_BACKORDER_QTY, SIM_DEMAND_QTY]}
        applyMeasures={applyMeasures}
        measureKeyMap={measureKeyMap}
      />
      {/* Sub Grid Measure Modal */}
      <MeasureModal
        open={subMeasureModalOpen}
        setOpen={setSubMeasureModalOpen}
        viewName={`${viewName}-sub`}
        allMeasure={subAllMeasure}
        requiredMeasure={[SIM_PRODUCTION_PLAN, CAPA_UTIL]}
        applyMeasures={applySubMeasures}
        measureKeyMap={measureKeyMap}
      />
      <MpProductionPlanDetailModal
        open={prodPlanDetailOpen}
        setOpen={setProdPlanDetailOpen}
        nodeData={prodPlanNodeData}
        selectVersion={mpVersionFilter}
      />
      <DimensAttrModal
        open={dimenModalOpen}
        close={handleDimenClose}
        viewName={`${viewName}_ETC`}
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        selectLv={{}}
        attrConfig={attrConfig}
      />
    </>
  );
};

export default withTranslation()(MpPsiSimulation);
